<template>
    <div class="d-inline-block h-100" :class="barColClass" @mousedown.stop>
        <div class="border bg-white shadow-sm rounded h-100 d-flex flex-column">
            <div class="d-flex align-items-center px-2 py-2 border-bottom">
                <div class="btn-group btn-group-sm w-100" role="group" aria-label="View toggle">
                    <button type="button" class="btn flex-fill" :class="viewMode === 'list' ? 'btn-primary' : ''"
                        title="List view" @click="setViewMode('list')">
                        <i class="fa-solid fa-list"></i>
                    </button>
                    <button type="button" class="btn flex-fill" :class="viewMode === 'grid' ? 'btn-primary' : ''"
                        title="Grid view" @click="setViewMode('grid')">
                        <i class="fa-solid fa-grip-vertical"></i>
                    </button>
                </div>
            </div>
            <div class="px-3 my-2">
                <div class="btn-group btn-group-sm dropright w-100" role="group">
                    <button type="button" class="btn dropdown-toggle w-100" data-toggle="dropdown" aria-haspopup="true"
                        aria-expanded="false" title="Filter">
                        {{ filterLabel }}
                    </button>
                    <div class="dropdown-menu">
                        <button class="dropdown-item" type="button" @click="setFilter('all')">All</button>
                        <button class="dropdown-item" type="button" @click="setFilter('lines')">Lines</button>
                        <button class="dropdown-item" type="button" @click="setFilter('shapes')">Shapes</button>
                        <button class="dropdown-item" type="button" @click="setFilter('fib')">Fib</button>
                        <button class="dropdown-item" type="button" @click="setFilter('tools')">Tools</button>
                        <button class="dropdown-item" type="button" @click="setFilter('stickers')">Stickers</button>
                    </div>
                </div>
            </div>
            <div class="px-2 pb-2 flex-grow-1 overflow-auto">
                <div v-if="viewMode === 'grid'" class="row no-gutters">
                    <div v-for="t in visibleTools" :key="t.key" class="col-6">
                        <button type="button" class="btn btn-sm btn-block text-center"
                            :class="activeTool === t.key ? 'btn-primary' : ''" :title="t.label" @click="emitTool(t)">
                            <i class="fa-solid" :class="t.icon"></i>
                        </button>
                    </div>
                </div>
                <div v-else class="list-group">
                    <button v-for="t in visibleTools" :key="t.key" type="button"
                        class="list-group-item list-group-item-action d-flex align-items-center"
                        :class="activeTool === t.key ? 'active' : ''" @click="emitTool(t)">
                        <i class="fa-solid mr-2" :class="t.icon"></i>
                        <span class="text-truncate text-sm">{{ t.label }}</span>
                    </button>
                </div>
            </div>
            <div class="px-2 py-2 border-top">
                <div class="btn-group btn-group-sm w-100" role="group">
                    <button type="button" class="btn flex-fill" title="Undo" @click="$emit('undo')">
                        <i class="fa-solid fa-rotate-left"></i>
                    </button>
                    <button type="button" class="btn flex-fill" title="Clear" @click="$emit('clear')">
                        <i class="fa-solid fa-trash"></i>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
const STORAGE_KEY = "kite_draw_toolbar_view";
export default {
    name: "DrawToolbar",
    props: { activeTool: { type: String, default: "" } },
    emits: ["select", "undo", "clear", "exit"],
    data() {
        return {
            filter: "all",
            viewMode: "grid",
            tools: [
                { key: "segment", label: "Trend Line", overlayName: "segment", group: "lines", icon: "fa-slash" },
                { key: "rayLine", label: "Ray", overlayName: "rayLine", group: "lines", icon: "fa-location-arrow" },
                { key: "straightLine", label: "Infinite Line", overlayName: "straightLine", group: "lines", icon: "fa-minus" },
                { key: "horizontalStraightLine", label: "Horizontal Line", overlayName: "horizontalStraightLine", group: "lines", icon: "fa-minus" },
                { key: "verticalStraightLine", label: "Vertical Line", overlayName: "verticalStraightLine", group: "lines", icon: "fa-grip-lines-vertical" },
                { key: "rect", label: "Rectangle", overlayName: "kiteRect", group: "shapes", icon: "fa-square" },
                { key: "ellipse", label: "Ellipse", overlayName: "kiteEllipse", group: "shapes", icon: "fa-circle" },
                { key: "annotation", label: "Annotation", overlayName: "kiteAnnotation", group: "shapes", icon: "fa-font" },
                { key: "arrowSticker", label: "Arrow", overlayName: "kiteArrowSticker", group: "tools", icon: "fa-arrow-right" },
                { key: "crossline", label: "Crossline", overlayName: "kiteCrossLine", group: "tools", icon: "fa-plus" },
                { key: "continuous", label: "Continuous", overlayName: "kiteContinuous", group: "tools", icon: "fa-wave-square" },
                { key: "doodle", label: "Doodle", overlayName: "kiteDoodle", group: "tools", icon: "fa-pen" },
                { key: "st_check", label: "Check", overlayName: "kiteSticker:check", group: "stickers", icon: "fa-check" },
                { key: "st_x", label: "Cross", overlayName: "kiteSticker:x", group: "stickers", icon: "fa-xmark" },
            ],
        };
    },
    mounted() {
        const saved = window.localStorage.getItem(STORAGE_KEY);
        if (saved === "grid" || saved === "list") this.viewMode = saved;
        if (window.$) window.$(this.$el).find('[data-toggle="dropdown"]').dropdown();
    },
    computed: {
        barColClass() {
            return this.viewMode === "grid" ? "col-2" : "col-4";
        },
        filterLabel() {
            return (
                {
                    all: "All",
                    lines: "Lines",
                    shapes: "Shapes",
                    fib: "Fib",
                    tools: "Tools",
                    stickers: "Stickers",
                }[this.filter] || "All"
            );
        },
        visibleTools() {
            return this.filter === "all" ? this.tools : this.tools.filter((t) => t.group === this.filter);
        },
    },
    methods: {
        setFilter(v) {
            this.filter = v;
        },
        setViewMode(mode) {
            this.viewMode = mode;
            window.localStorage.setItem(STORAGE_KEY, mode);
        },
        emitTool(t) {
            this.$emit("select", t);
        },
    },
};
</script>
